import { readRecord } from '@/lib/prop-utils';
import type { Message } from '@/types/assistant';

const NEEDS_CONFIRMATION_STATUS = 'needs_confirmation';

export interface ImageGenerationConfirmation {
  status: typeof NEEDS_CONFIRMATION_STATUS;
  confirmationId: string;
  kind: 'attorney_or_staff' | 'client' | 'mixed';
  message: string;
  choices: Array<{ id: 'alternative' | 'continue_fictional'; label: string }>;
}

export interface ImageGenerationAcknowledgement {
  confirmationId: string;
  action: 'continue_fictional';
}

export function readImageGenerationConfirmation(
  value: unknown,
): ImageGenerationConfirmation | null {
  if (typeof value === 'string') {
    try {
      return readImageGenerationConfirmation(JSON.parse(value));
    } catch {
      return null;
    }
  }
  const record = readRecord(value);
  if (
    !record ||
    record.status !== NEEDS_CONFIRMATION_STATUS ||
    typeof record.confirmationId !== 'string' ||
    !['attorney_or_staff', 'client', 'mixed'].includes(String(record.kind)) ||
    typeof record.message !== 'string' ||
    !Array.isArray(record.choices) ||
    record.choices.length === 0
  )
    return null;
  const choices: ImageGenerationConfirmation['choices'] = [];
  for (const value of record.choices) {
    const choice = readRecord(value);
    if (
      !choice ||
      (choice.id !== 'alternative' && choice.id !== 'continue_fictional') ||
      typeof choice.label !== 'string'
    )
      return null;
    choices.push({ id: choice.id, label: choice.label });
  }
  return {
    status: NEEDS_CONFIRMATION_STATUS,
    confirmationId: record.confirmationId,
    kind: record.kind as ImageGenerationConfirmation['kind'],
    message: record.message,
    choices,
  };
}

export function findPendingImageConfirmation(
  items: ReadonlyArray<{
    role?: string;
    type?: string;
    imageConfirmation?: ImageGenerationConfirmation;
  }>,
): ImageGenerationConfirmation | undefined {
  for (let index = items.length - 1; index >= 0; index--) {
    const item = items[index];
    if (item.role === 'user' || item.type === 'user') return undefined;
    if (item.imageConfirmation) return item.imageConfirmation;
  }
  return undefined;
}

export function imageGenerationAcknowledgement(
  confirmation: ImageGenerationConfirmation | undefined,
  selectedText: string,
): ImageGenerationAcknowledgement | undefined {
  if (
    !confirmation?.choices.some(
      (choice) => choice.id === 'continue_fictional' && choice.label === selectedText,
    )
  )
    return undefined;
  return { confirmationId: confirmation.confirmationId, action: 'continue_fictional' };
}

export function imagePolicyDisplayMessages(message: Message): Message[] {
  if (message.role !== 'tool') return [message];
  const output = message.toolResult?.rawOutput;
  const confirmation = readImageGenerationConfirmation(output);
  if (confirmation) {
    return [
      {
        ...message,
        role: 'assistant',
        content: confirmation.message,
        synthetic: true,
        imageConfirmation: confirmation,
      },
    ];
  }
  const record = readRecord(output);
  if (record?.success === true && typeof record.note === 'string') {
    return [
      message,
      {
        role: 'assistant',
        content: record.note,
        timestamp: message.timestamp + 1,
        fromHistory: message.fromHistory,
        synthetic: true,
        toolCallId: message.toolCallId,
      },
    ];
  }
  return [message];
}
